import Paper from '@mui/material/Paper';
import { L } from 'harmony-language';
import React from 'react';
import { OrgQueryKeys } from '../../../../../api/config';
import { useOrganizationQuery } from '../../../../../api/queries/use-organization-query';
import '../../../../../scss/bulk-creation.scss';
import { OrganizationLocation } from '../../../../../types';
import { createAgisticsSelectElements } from '../../../../shared/agistics-select';
import type { BulkConfigurationSettings, BulkDraftProjection, SiteProjection } from '../bulk-drafts-types';
import { BulkEntryTable } from './bulk-entry-table';
import { BulkEntryTitleBar } from './bulk-entry-title-bar';
import { getDaysOfWorkingWeek } from '../../../../../utils/date-time';

interface BulkEntryProps {
    bulkConfiguration: BulkConfigurationSettings;
    previewLoads: (finalProjections: BulkDraftProjection[]) => void;
    handleBack: () => void;
}

export const BulkEntry = (props: BulkEntryProps) => {
    const { bulkConfiguration, previewLoads, handleBack } = props;

    const { data: locations = [] } = useOrganizationQuery<OrganizationLocation[]>(OrgQueryKeys.locations);
    const [siteProjections, setSiteProjections] = React.useState<SiteProjection[]>([]);
    
    const sitesOfSiteType = React.useMemo(() => locations
        ?.filter(companyLocation => bulkConfiguration.siteType.id === companyLocation.orgLocationType?.id
            && !siteProjections.some(projection => projection.orgLocation.id === companyLocation.id)
        )
        .sortBy(companyLocation => companyLocation.name)
        || []
    , [locations, siteProjections]);
    
    const siteSelectItems = createAgisticsSelectElements(sitesOfSiteType, (orgLocation) => orgLocation.name);

    function locationSelectHandler(id: number) {
        id > 0 && setSiteProjections(prevProjections => [...prevProjections, {
            orgLocation: sitesOfSiteType.find(location => location.id === id)!,
            projectionWeek: initializeProjectionWeeks(bulkConfiguration.weeks)
        }]);
    }

    return (
        <div className='bulk-entry' data-testid='bulk-entry'>
            <h2>{L.bulkEntryHeading1()}
                {bulkConfiguration.loadSize && <small style={{ marginLeft: '1rem'}}>{L.bulkEntryHeading2(bulkConfiguration.loadSize)}</small>}
            </h2>
            <Paper>
                <BulkEntryTitleBar
                    siteType={bulkConfiguration.siteType}
                    siteProjections={siteProjections}
                    previewLoads={() => previewLoads(createProjections(siteProjections, bulkConfiguration))}
                    handleBack={handleBack}
                />
                <BulkEntryTable
                    siteSelectItems={siteSelectItems}
                    siteProjections={siteProjections}
                    setSiteProjections={setSiteProjections}
                    locationSelectHandler={locationSelectHandler}
                    isOriginBased={bulkConfiguration.checked}
                />
            </Paper>
        </div>
    );
};

function initializeProjectionWeeks(weeks: string[]) {
    return weeks.map(week => ({
        week: week,
        schedule: [...Array(7)].map(() => 0)
    }));
}

function createProjections(siteProjections: SiteProjection[], bulkConfiguration: BulkConfigurationSettings) {
    const finalProjections: BulkDraftProjection[] = [];
    
    siteProjections.forEach(siteProjection => {
        siteProjection.projectionWeek.forEach(projectionWeek => {
            const weekDays = getDaysOfWorkingWeek(projectionWeek.week);

            weekDays.forEach((day, dayIndex) => {
                if (projectionWeek.schedule[dayIndex] > 0) {
                    finalProjections.push({
                        orgLocation: siteProjection.orgLocation,
                        cargoType: bulkConfiguration.cargoType,
                        date: day.value,
                        numberOfLoads: projectionWeek.schedule[dayIndex],
                        weightPerLoad: bulkConfiguration.loadWeightGrams,
                        quantityPerLoad: bulkConfiguration.loadSize,
                        isBasedOnOrigin: bulkConfiguration.checked,
                    });
                }
            });
        });
    });

    // Important for display and getting min/max dates
    finalProjections.sort((a, b) => {
        if (a.date.isSame(b.date)) {
            return a.orgLocation.name.localeCompare(b.orgLocation.name);
        }
        return a.date.isAfter(b.date) ? 1 : -1;
    });

    return finalProjections;
}